import { useMutation } from "convex/react";
import { SmilePlus } from "lucide-react";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const QUICK_EMOJIS = ["👍", "❤️", "😂", "🎉", "😮", "😢", "🔥", "👀", "✅", "🙏"];

type Reaction = { emoji: string; count: number; mine: boolean };

export function ReactionPills({
  messageId,
  reactions,
  align = "start",
}: {
  messageId: Id<"messages">;
  reactions: Reaction[];
  align?: "start" | "end";
}) {
  const toggle = useMutation(api.reactions.toggle);
  if (reactions.length === 0) return null;

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-1",
        align === "end" ? "justify-end" : "justify-start",
      )}
    >
      {reactions.map((r) => (
        <button
          key={r.emoji}
          onClick={() => toggle({ messageId, emoji: r.emoji })}
          className={cn(
            "h-6 px-1.5 inline-flex items-center gap-1 rounded-full border text-xs transition shadow-sm",
            r.mine
              ? "bg-primary/15 border-primary/40"
              : "bg-background border-border hover:bg-accent",
          )}
        >
          <span>{r.emoji}</span>
          <span className="tabular-nums">{r.count}</span>
        </button>
      ))}
    </div>
  );
}

export function AddReactionButton({
  messageId,
  align = "start",
}: {
  messageId: Id<"messages">;
  align?: "start" | "end";
}) {
  const toggle = useMutation(api.reactions.toggle);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="size-7 text-muted-foreground"
          aria-label="Add reaction"
        >
          <SmilePlus className="size-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-1.5" align={align}>
        <div className="grid grid-cols-5 gap-0.5">
          {QUICK_EMOJIS.map((e) => (
            <button
              key={e}
              onClick={() => toggle({ messageId, emoji: e })}
              className="size-8 rounded hover:bg-accent text-lg leading-none"
            >
              {e}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
